/**
 * RP-28.3 (v1) — "Dates changed since staffing". A date change on a plan whose Resourcing
 * status is Staffed writes nothing to the Solution: nobody's Assignment moves and no
 * Intensity changes. This note is how the RM finds out, so they can decide what moves.
 * Shown only to those who can set the Resourcing status (RM / ADMIN).
 */

import { CalendarClock } from "lucide-react";
import { longMonth } from "@/components/plans/PlanDatesDialog";
import { usePlanDatesStaffingReview } from "@/hooks/use-plan-dates";
import { formatDateYMD } from "@/lib/format";
import type { ResourcePlanDetailView } from "@/schemas/resource-plans";

function range(start: string, end: string | null): string {
  return `${longMonth(start)} – ${end === null ? "Planning Horizon" : longMonth(end)}`;
}

export function PlanDatesStaffingNotice({ plan }: { plan: ResourcePlanDetailView }) {
  const enabled = plan.resourcingStatus === "Staffed" && plan.canSetResourcingStatus === true;
  const review = usePlanDatesStaffingReview(plan.id, enabled);
  const change = review.data?.lastChange ?? null;
  if (!enabled || review.data?.changedSinceStaffing !== true || change === null) return null;
  return (
    <div
      role="note"
      className="flex items-start gap-2.5 rounded-md border border-warning-200 bg-warning-100 px-3 py-2 text-sm text-warning-700 dark:text-warning-400"
    >
      <CalendarClock className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      <p>
        <span className="font-medium">Dates changed since staffing.</span>{" "}
        {change.actorName ?? "Someone"} moved this plan from{" "}
        {range(change.fromStartMonth, change.fromEndMonth)} to{" "}
        {range(change.toStartMonth, change.toEndMonth)} on {formatDateYMD(change.changedAt)}
        {change.reason === null ? "" : ` — “${change.reason}”`}. Nobody on the Solution has changed;
        check the Pod still fits the new dates.
      </p>
    </div>
  );
}
